import type { BrandSettings, ReportContent } from '@quickreport/domain';
import { assembleReport, type SignatoryRow } from '@quickreport/pdf';
import type { ReportRecord } from '@quickreport/workspace';
import type { LocalDb } from '../../local-db/client';
import { loadImage } from '../../local-db/files';
import { loadBrand } from '../../local-db/settings';
import { renderPdf } from '../../pdf/client';

export interface ReportPdf {
  pdf: Uint8Array;
  /** Whether the printed page carries a verification QR (only once the server has issued a code). */
  hasQr: boolean;
  ms: number;
}

/**
 * Render a saved report exactly as it prints: lab settings, logo, signatures, and a
 * verification QR only when the server has issued the report's code. The Android app prints
 * through the same assembleReport, so both make the same PDF.
 */
export async function renderReportPdf(
  db: LocalDb,
  orgId: string,
  orgName: string,
  verifyBaseUrl: string,
  report: Pick<ReportRecord, 'labNo' | 'status' | 'verifyCode' | 'updatedAt'> & {
    content: ReportContent;
    version?: number;
  },
  brandOverride?: BrandSettings,
): Promise<ReportPdf> {
  const { data, assets, hasQr } = await assembleReport(
    report,
    {
      verifyBaseUrl,
      currentBrand: await loadBrand(db, orgId, { labName: orgName }),
      loadImage: (id) => loadImage(db, id),
      signatories: async (ids) =>
        (
          await db.query<SignatoryRow>(
            'select id, name, qualification, designation, reg_no, signature_file_id from signatories where id = any($1::uuid[])',
            [ids],
          )
        ).rows,
    },
    brandOverride,
  );
  const res = await renderPdf(data, assets);
  if (res.error || !res.pdf) throw new Error(res.error ?? 'The PDF could not be made');
  return { pdf: res.pdf, hasQr, ms: res.ms };
}
